/**
 * Stage 2 client calls for one lead: resume upload / download / delete /
 * re-parse and the candidate contact PATCH (docs/BUILD_PLAN.md → Stage 2).
 *
 * The upload and download bypass `ApiClient` on purpose — it forces a JSON
 * body and parses the response as JSON. Both still send the bearer and
 * `X-Innu-App: recruiting`, and retry once after a 401 refresh.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, ApiError, useApiClient, type ApiClient } from '@/lib/api-client';
import { getStoredSelectedCompanyId, useAuth } from '@/lib/auth';
import { LEADS_KEY, leadKey } from '@/features/pipeline/api';
import type { ResumeSummary } from '@/features/pipeline/types';

export const RESUME_MAX_BYTES = 10 * 1024 * 1024;
export const RESUME_ACCEPT = '.pdf,.doc,.docx';
const RESUME_EXT = /\.(pdf|docx?)$/i;

/** Client-side gate so an obviously wrong file never leaves the browser. */
export function resumeFileProblem(file: File): string | null {
  if (!RESUME_EXT.test(file.name)) return 'Only PDF, .doc and .docx files are accepted.';
  if (file.size > RESUME_MAX_BYTES) return 'The file is larger than 10 MB.';
  if (file.size === 0) return 'The file is empty.';
  return null;
}

interface AuthLike {
  getToken: () => string | null;
  refresh: () => Promise<string | null>;
}

async function authedFetch(auth: AuthLike, path: string, init: RequestInit): Promise<Response> {
  const send = (token: string | null) => {
    const headers = new Headers(init.headers);
    headers.set('X-Innu-App', 'recruiting');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const companyId = getStoredSelectedCompanyId();
    if (companyId) headers.set('X-Selected-Company-Id', companyId);
    return fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  };
  let res = await send(auth.getToken());
  if (res.status === 401) {
    const fresh = await auth.refresh();
    if (fresh) res = await send(fresh);
  }
  return res;
}

async function throwFor(res: Response, path: string): Promise<never> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON error body
  }
  const err = (body as { error?: { code?: string; message?: string } } | null)?.error;
  throw new ApiError(res.status, body, err?.message ?? `API ${res.status} — ${path}`, err?.code);
}

export async function uploadResume(auth: AuthLike, leadId: string, file: File): Promise<ResumeSummary | null> {
  const form = new FormData();
  form.append('file', file, file.name);
  const path = `/api/leads/${leadId}/resume`;
  const res = await authedFetch(auth, path, { method: 'POST', body: form });
  if (!res.ok) return throwFor(res, path);
  const json = (await res.json().catch(() => null)) as { data?: { resume?: ResumeSummary | null } } | null;
  return json?.data?.resume ?? null;
}

/** Streams the file through the bearer and hands it to the browser as a download. */
export async function downloadResume(auth: AuthLike, leadId: string, filename: string): Promise<void> {
  const path = `/api/leads/${leadId}/resume`;
  const res = await authedFetch(auth, path, { method: 'GET' });
  if (!res.ok) return throwFor(res, path);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'resume';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export interface CandidatePatch {
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string;
}

export function patchCandidate(client: ApiClient, leadId: string, patch: CandidatePatch) {
  return client.patch<unknown>(`/api/leads/${leadId}/candidate`, patch);
}

/** Every resume/candidate mutation for a lead; each one refetches the lead and the board. */
export function useLeadProfileMutations(leadId: string) {
  const auth = useAuth();
  const client = useApiClient();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: leadKey(leadId) });
    void qc.invalidateQueries({ queryKey: LEADS_KEY });
  };
  const upload = useMutation({ mutationFn: (file: File) => uploadResume(auth, leadId, file), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: () => client.delete<unknown>(`/api/leads/${leadId}/resume`),
    onSuccess: refresh,
  });
  const reparse = useMutation({
    mutationFn: () => client.post<unknown>(`/api/leads/${leadId}/resume/parse`),
    onSuccess: refresh,
  });
  const download = useMutation({
    mutationFn: (filename: string) => downloadResume(auth, leadId, filename),
  });
  const candidate = useMutation({
    mutationFn: (patch: CandidatePatch) => patchCandidate(client, leadId, patch),
    onSuccess: refresh,
  });
  return { upload, remove, reparse, download, candidate };
}
